// Error handling module with multilingual support
export interface ErrorInfo {
  code: string;
  message: string;
  details?: string;
  field?: string;
  timestamp: Date;
  stack?: string;
  context?: Record<string, any>;
}

export interface LocalizedError {
  [key: string]: {
    title: string;
    message: string;
    action?: string;
    suggestion?: string;
  };
}

// Base error class for the application
export class AppError extends Error {
  public readonly code: string;
  public readonly details?: string;
  public readonly field?: string;
  public readonly timestamp: Date;
  public readonly context?: Record<string, any>;
  public readonly isOperational: boolean;

  constructor(
    code: string,
    message: string,
    details?: string,
    field?: string,
    context?: Record<string, any>,
    isOperational = true,
  ) {
    super(message);
    this.name = "AppError";
    this.code = code;
    this.details = details;
    this.field = field;
    this.timestamp = new Date();
    this.context = context;
    this.isOperational = isOperational;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }

  toJSON(): ErrorInfo {
    return {
      code: this.code,
      message: this.message,
      details: this.details,
      field: this.field,
      timestamp: this.timestamp,
      stack: this.stack,
      context: this.context,
    };
  }
}

// Specific error types
export class ValidationError extends AppError {
  constructor(message: string, field?: string, details?: string) {
    super("VALIDATION_ERROR", message, details, field);
    this.name = "ValidationError";
  }
}

export class AuthenticationError extends AppError {
  constructor(message: string, details?: string) {
    super("AUTHENTICATION_ERROR", message, details);
    this.name = "AuthenticationError";
  }
}

export class AuthorizationError extends AppError {
  constructor(message: string, details?: string) {
    super("AUTHORIZATION_ERROR", message, details);
    this.name = "AuthorizationError";
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string, details?: string) {
    super("NOT_FOUND_ERROR", `${resource} not found`, details);
    this.name = "NotFoundError";
  }
}

export class NetworkError extends AppError {
  constructor(message: string, details?: string) {
    super("NETWORK_ERROR", message, details);
    this.name = "NetworkError";
  }
}

export class ServerError extends AppError {
  constructor(message: string, details?: string) {
    super("SERVER_ERROR", message, details);
    this.name = "ServerError";
  }
}

export class DatabaseError extends AppError {
  constructor(message: string, details?: string) {
    super("DATABASE_ERROR", message, details);
    this.name = "DatabaseError";
  }
}

export class BusinessLogicError extends AppError {
  constructor(message: string, details?: string) {
    super("BUSINESS_LOGIC_ERROR", message, details);
    this.name = "BusinessLogicError";
  }
}

// Error codes mapping
export const ERROR_CODES = {
  // Validation errors (1000-1999)
  VALIDATION_ERROR: "1000",
  REQUIRED_FIELD: "1001",
  INVALID_EMAIL: "1002",
  INVALID_PHONE: "1003",
  INVALID_DATE: "1004",
  INVALID_FORMAT: "1005",
  FIELD_TOO_SHORT: "1006",
  FIELD_TOO_LONG: "1007",
  INVALID_CHOICE: "1008",

  // Authentication errors (2000-2999)
  AUTHENTICATION_ERROR: "2000",
  INVALID_CREDENTIALS: "2001",
  TOKEN_EXPIRED: "2002",
  TOKEN_INVALID: "2003",
  SESSION_EXPIRED: "2004",
  ACCOUNT_LOCKED: "2005",
  TOO_MANY_ATTEMPTS: "2006",

  // Authorization errors (3000-3999)
  AUTHORIZATION_ERROR: "3000",
  INSUFFICIENT_PERMISSIONS: "3001",
  ACCESS_DENIED: "3002",
  ROLE_REQUIRED: "3003",

  // Not found errors (4000-4999)
  NOT_FOUND_ERROR: "4000",
  USER_NOT_FOUND: "4001",
  RESOURCE_NOT_FOUND: "4002",
  FILE_NOT_FOUND: "4003",

  // Network errors (5000-5999)
  NETWORK_ERROR: "5000",
  REQUEST_TIMEOUT: "5001",
  CONNECTION_FAILED: "5002",
  API_UNAVAILABLE: "5003",

  // Server errors (6000-6999)
  SERVER_ERROR: "6000",
  INTERNAL_ERROR: "6001",
  SERVICE_UNAVAILABLE: "6002",
  MAINTENANCE_MODE: "6003",

  // Database errors (7000-7999)
  DATABASE_ERROR: "7000",
  CONNECTION_ERROR: "7001",
  QUERY_ERROR: "7002",
  CONSTRAINT_VIOLATION: "7003",

  // Business logic errors (8000-8999)
  BUSINESS_LOGIC_ERROR: "8000",
  INVALID_OPERATION: "8001",
  STATE_CONFLICT: "8002",
  RESOURCE_CONFLICT: "8003",

  // Unknown errors (9000-9999)
  UNKNOWN_ERROR: "9000",
} as const;

export type ErrorCode = keyof typeof ERROR_CODES;

// Error factory functions
export const createError = {
  validation: (message: string, field?: string, details?: string) =>
    new ValidationError(message, field, details),

  authentication: (message: string, details?: string) =>
    new AuthenticationError(message, details),

  authorization: (message: string, details?: string) =>
    new AuthorizationError(message, details),

  notFound: (resource: string, details?: string) =>
    new NotFoundError(resource, details),

  network: (message: string, details?: string) =>
    new NetworkError(message, details),

  server: (message: string, details?: string) =>
    new ServerError(message, details),

  database: (message: string, details?: string) =>
    new DatabaseError(message, details),

  businessLogic: (message: string, details?: string) =>
    new BusinessLogicError(message, details),
};

// Error severity levels
export enum ErrorSeverity {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

// Error categories
export enum ErrorCategory {
  VALIDATION = "validation",
  AUTHENTICATION = "authentication",
  AUTHORIZATION = "authorization",
  NOT_FOUND = "not_found",
  NETWORK = "network",
  SERVER = "server",
  DATABASE = "database",
  BUSINESS_LOGIC = "business_logic",
  UNKNOWN = "unknown",
}

// Error context interface
export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  requestId?: string;
  url?: string;
  method?: string;
  userAgent?: string;
  timestamp: Date;
  severity: ErrorSeverity;
  category: ErrorCategory;
}

// Error logger interface
export interface ErrorLogger {
  log(error: AppError, context?: ErrorContext): void;
  logError(error: Error, context?: ErrorContext): void;
  logWarning(message: string, context?: ErrorContext): void;
  logInfo(message: string, context?: ErrorContext): void;
}

// Error handler interface
export interface ErrorHandler {
  handle(error: AppError | Error, context?: ErrorContext): void;
  handleAsync<T>(promise: Promise<T>, context?: ErrorContext): Promise<T>;
  isOperational(error: AppError | Error): boolean;
}

// Error recovery strategies
export interface ErrorRecoveryStrategy {
  canRecover(error: AppError): boolean;
  recover(error: AppError, context?: ErrorContext): Promise<void>;
}

// Error boundary state
export interface ErrorBoundaryState {
  hasError: boolean;
  error?: AppError | Error;
  errorInfo?: React.ErrorInfo;
  errorId?: string;
}

// Error reporting service interface
export interface ErrorReportingService {
  report(error: AppError, context?: ErrorContext): Promise<void>;
  reportError(error: Error, context?: ErrorContext): Promise<void>;
  setUser(userId: string, userInfo?: Record<string, any>): void;
  setContext(context: Record<string, any>): void;
}

// Error utility functions
export const errorUtils = {
  // Check if error is operational (can be handled gracefully)
  isOperational: (error: AppError | Error): boolean => {
    if (error instanceof AppError) {
      return error.isOperational;
    }
    return false;
  },

  // Get error category from error code
  getCategory: (code: string): ErrorCategory => {
    const codeNum = parseInt(code);
    if (codeNum >= 1000 && codeNum < 2000) return ErrorCategory.VALIDATION;
    if (codeNum >= 2000 && codeNum < 3000) return ErrorCategory.AUTHENTICATION;
    if (codeNum >= 3000 && codeNum < 4000) return ErrorCategory.AUTHORIZATION;
    if (codeNum >= 4000 && codeNum < 5000) return ErrorCategory.NOT_FOUND;
    if (codeNum >= 5000 && codeNum < 6000) return ErrorCategory.NETWORK;
    if (codeNum >= 6000 && codeNum < 7000) return ErrorCategory.SERVER;
    if (codeNum >= 7000 && codeNum < 8000) return ErrorCategory.DATABASE;
    if (codeNum >= 8000 && codeNum < 9000) return ErrorCategory.BUSINESS_LOGIC;
    return ErrorCategory.UNKNOWN;
  },

  // Get error severity based on category and context
  getSeverity: (error: AppError, context?: ErrorContext): ErrorSeverity => {
    const category = errorUtils.getCategory(error.code);

    switch (category) {
      case ErrorCategory.CRITICAL:
      case ErrorCategory.SERVER:
      case ErrorCategory.DATABASE:
        return ErrorSeverity.CRITICAL;
      case ErrorCategory.AUTHENTICATION:
      case ErrorCategory.AUTHORIZATION:
        return ErrorSeverity.HIGH;
      case ErrorCategory.NETWORK:
      case ErrorCategory.BUSINESS_LOGIC:
        return ErrorSeverity.MEDIUM;
      case ErrorCategory.VALIDATION:
      case ErrorCategory.NOT_FOUND:
        return ErrorSeverity.LOW;
      default:
        return ErrorSeverity.MEDIUM;
    }
  },

  // Generate unique error ID
  generateErrorId: (): string => {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  // Sanitize error for logging (remove sensitive data)
  sanitize: (error: AppError): AppError => {
    const sanitized = new AppError(
      error.code,
      error.message,
      error.details,
      error.field,
      error.context,
    );

    // Remove sensitive data from context
    if (sanitized.context) {
      const sensitiveKeys = ["password", "token", "secret", "key"];
      sensitiveKeys.forEach((key) => {
        if (sanitized.context![key]) {
          sanitized.context![key] = "[REDACTED]";
        }
      });
    }

    return sanitized;
  },

  // Format error for display
  formatForDisplay: (error: AppError | Error): string => {
    if (error instanceof AppError) {
      return `${error.code}: ${error.message}`;
    }
    return error.message || "An unknown error occurred";
  },

  // Check if error should be retried
  shouldRetry: (
    error: AppError,
    attempt: number,
    maxAttempts: number = 3,
  ): boolean => {
    if (attempt >= maxAttempts) return false;

    const retryableCategories = [
      ErrorCategory.NETWORK,
      ErrorCategory.SERVER,
      ErrorCategory.DATABASE,
    ];

    return retryableCategories.includes(errorUtils.getCategory(error.code));
  },

  // Get retry delay based on attempt number
  getRetryDelay: (attempt: number, baseDelay: number = 1000): number => {
    return baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
  },
};
